import type { CodeSessionAgentEvent } from '@/lib/code-sessions/types';
import type { SessionMessageInfo } from '@opencode/client/promise';

import type { SessionPart } from './transcript-model';

import { getRecord } from '@/lib/utils/unknown';

export type ResponseRetryStatus = 'retrying' | 'recovered' | 'stopped';
/**
 * A failed attempt as OpenCode records it (`Session.StructuredError`): its kind, its message,
 * which matches a later failure to the retry and is never shown, and the provider's HTTP status.
 * `response.body` is the provider's raw answer, kept for a run that fails while a retry is pending
 * (`pendingError`): a subscription's card reads its code there. The retry row never shows either.
 */
type RetryError = {
  type: string;
  message: string;
  status?: number;
  response?: { body: string };
};
type ResponseRetry = {
  /** Each distinct error seen, for matching a failure to the retry; a repeat is not added again. */
  errors: RetryError[];
  /** When the latest scheduled attempt is made. */
  at: number;
  /** The error of the latest scheduled attempt, whose status and kind the row words as its reason. */
  latest: RetryError;
  opencodeSeq?: number;
  /** Earliest live event sequence for the retried response. */
  responseSeq?: number;
};

const RESPONSE_RETRY_LABELS: Record<ResponseRetryStatus, string> = {
  retrying: 'Response interrupted. Retrying…',
  recovered: 'Response interrupted. Recovered.',
  stopped: 'Response interrupted.',
};

function retryError(error: unknown): RetryError | undefined {
  const record = getRecord(error);
  if (typeof record?.type !== 'string' || typeof record.message !== 'string') {
    return undefined;
  }
  const body = getRecord(record.response)?.body;
  return {
    type: record.type,
    message: record.message,
    ...(typeof record.status === 'number' ? { status: record.status } : {}),
    ...(typeof body === 'string' ? { response: { body } } : {}),
  };
}

/**
 * Why a response is retried, in cubic's words: from the failed attempt's HTTP status and the kind
 * OpenCode 2.0.26 gives it (`toSessionError`), never from its message or its response body, which
 * carry the provider's own words, such as an account and its limits. A 429 is cubic's own limit on
 * the model route on this computer as much as a provider's. A failure neither names gives no
 * reason, and the row says only when the next attempt is.
 */
function retryReason({ type, status }: Pick<RetryError, 'type' | 'status'>): string | undefined {
  if (status === 429 || type === 'provider.rate-limit') {
    return 'Too many requests right now';
  }
  if (status === 408 || status === 504 || type === 'provider.timeout') {
    return 'The model took too long to answer';
  }
  if ((status !== undefined && status >= 500) || type === 'provider.internal') {
    return 'The model provider had a temporary error';
  }
  if (type === 'provider.transport') {
    return 'The connection to the model dropped';
  }
  return undefined;
}

function matchesResponseRetry(
  error: unknown,
  retry: ResponseRetry | undefined,
  failure?: { at: number; opencodeSeq?: number },
): boolean {
  const parsed = retryError(error);
  if (
    parsed === undefined ||
    retry === undefined ||
    !retry.errors.some(
      (candidate) => candidate.type === parsed.type && candidate.message === parsed.message,
    )
  ) {
    return false;
  }
  // A snapshot can already contain the schedule while its live event has not
  // arrived. A later failure from an exhausted attempt is still terminal.
  return (
    failure === undefined ||
    (failure.opencodeSeq !== undefined && retry.opencodeSeq !== undefined
      ? failure.opencodeSeq < retry.opencodeSeq
      : failure.at <= retry.at)
  );
}

export function successfulResponseFinish(finish: unknown): boolean {
  return (
    finish === 'stop' || finish === 'tool-calls' || finish === 'length' || finish === 'end_turn'
  );
}

/** Index retry evidence; the transcript's ordered fold drives its lifecycle. */
function responseRetries(
  messages: readonly SessionMessageInfo[],
  events: readonly CodeSessionAgentEvent[],
): { byMessage: Map<string, ResponseRetry>; unexecutedTools: ReadonlySet<string> } {
  const byMessage = new Map<string, ResponseRetry>();
  const record = (messageId: string, error: unknown, at: number, opencodeSeq?: number) => {
    const parsed = retryError(error);
    if (parsed === undefined) {
      return;
    }
    const retry: ResponseRetry = byMessage.get(messageId) ?? { errors: [], at, latest: parsed };
    const existing = retry.errors.find(
      (candidate) => candidate.type === parsed.type && candidate.message === parsed.message,
    );
    if (existing === undefined) {
      retry.errors.push(parsed);
    } else if (existing.response === undefined && parsed.response !== undefined) {
      existing.response = parsed.response;
    }
    if (at >= retry.at) {
      retry.latest = parsed;
    }
    retry.at = Math.max(retry.at, at);
    if (opencodeSeq !== undefined) {
      retry.opencodeSeq = Math.max(retry.opencodeSeq ?? opencodeSeq, opencodeSeq);
    }
    byMessage.set(messageId, retry);
  };
  for (const message of messages) {
    if (message.type === 'assistant' && message.retry !== undefined) {
      record(message.id, message.retry.error, message.retry.at);
    }
  }
  for (const event of events) {
    const data = getRecord(event.data);
    if (event.type === 'session.retry.scheduled' && typeof data?.assistantMessageID === 'string') {
      record(
        data.assistantMessageID,
        data.error,
        typeof data.at === 'number' ? data.at : event.at,
        event.opencodeSeq,
      );
    }
  }
  for (const event of events) {
    const messageId = getRecord(event.data)?.assistantMessageID;
    if (typeof messageId !== 'string' || event.opencodeSeq === undefined) {
      continue;
    }
    const retry = byMessage.get(messageId);
    if (retry !== undefined) {
      retry.responseSeq = Math.min(retry.responseSeq ?? event.opencodeSeq, event.opencodeSeq);
    }
  }

  // Provider failures can strand a streamed tool input before the call runs.
  // That is the interrupted response, not an edit or command that failed.
  const unexecutedTools = new Set<string>();
  if (byMessage.size === 0) {
    return { byMessage, unexecutedTools };
  }
  for (const message of messages) {
    if (message.type !== 'assistant') {
      continue;
    }
    for (const content of message.content) {
      if (
        content.type === 'tool' &&
        content.state.status === 'error' &&
        content.time?.ran === undefined &&
        matchesResponseRetry(content.state.error, byMessage.get(message.id))
      ) {
        unexecutedTools.add(`${message.id}:tool:${content.id}`);
      }
    }
  }
  for (const event of events) {
    const data = getRecord(event.data);
    if (
      event.type !== 'session.tool.failed' ||
      typeof data?.assistantMessageID !== 'string' ||
      typeof data.id !== 'string'
    ) {
      continue;
    }
    const key = `${data.assistantMessageID}:tool:${data.id}`;
    if (
      data.executed === false &&
      matchesResponseRetry(data.error, byMessage.get(data.assistantMessageID))
    ) {
      unexecutedTools.add(key);
    } else if (data.executed === true) {
      unexecutedTools.delete(key);
    }
  }
  return { byMessage, unexecutedTools };
}

type RetryNotice = Extract<SessionPart, { type: 'status' }> & {
  id: string;
  messageId: string;
  retry: ResponseRetryStatus;
};

/** Keep retry evidence, notices and their transitions together within one timeline. */
export function responseRetryFold(
  messages: readonly SessionMessageInfo[],
  events: readonly CodeSessionAgentEvent[],
) {
  const evidence = responseRetries(messages, events);
  const notices = new Map<string, { part: RetryNotice; turnId?: string; opencodeSeq?: number }>();

  return {
    unexecutedTools: evidence.unexecutedTools,
    notice(
      messageId: string,
      error: unknown,
      turnId?: string,
      failure?: { at: number; opencodeSeq?: number },
    ): RetryNotice | undefined {
      const retry = evidence.byMessage.get(messageId);
      if (!matchesResponseRetry(error, retry, failure)) {
        return undefined;
      }
      const existing = notices.get(messageId);
      if (existing !== undefined) {
        return existing.part;
      }
      // The evidence was indexed whole before the fold, so the latest attempt's reason and time
      // are known here. They stay while the retry is pending and go with it; the row shows them
      // until the next attempt is due (`StatusRow`).
      const reason = retry === undefined ? undefined : retryReason(retry.latest);
      const part: RetryNotice = {
        type: 'status',
        id: `${messageId}:retry`,
        messageId,
        label: RESPONSE_RETRY_LABELS.retrying,
        retry: 'retrying',
        running: true,
        ...(retry === undefined ? {} : { nextAttemptAt: retry.at }),
        ...(reason === undefined ? {} : { reason }),
      };
      notices.set(messageId, {
        part,
        turnId,
        opencodeSeq: retry?.opencodeSeq ?? retry?.responseSeq,
      });
      return part;
    },
    /**
     * A later completed response proves recovery; a new prompt or terminal run
     * bounds it. Merely starting another response is not evidence of success.
     * Read both snapshots and live retry events so reconnects keep the same state.
     */
    settle(status: 'recovered' | 'stopped', turnId?: string, opencodeSeq?: number): void {
      for (const retry of notices.values()) {
        if (retry.part.retry !== 'retrying' || (turnId !== undefined && retry.turnId !== turnId)) {
          continue;
        }
        // Delivery timestamps can put an old terminal event after the retry.
        // Only a newer OpenCode event can settle it; a retry known only from the
        // snapshot is at least as new as its response's first event.
        if (
          opencodeSeq !== undefined &&
          retry.opencodeSeq !== undefined &&
          opencodeSeq < retry.opencodeSeq
        ) {
          continue;
        }
        retry.part.retry = status;
        retry.part.label = RESPONSE_RETRY_LABELS[status];
        retry.part.running = undefined;
        delete retry.part.reason;
        delete retry.part.nextAttemptAt;
      }
    },
    pendingError(): RetryError | undefined {
      const pending = [...notices].findLast(([, retry]) => retry.part.retry === 'retrying');
      return pending === undefined ? undefined : evidence.byMessage.get(pending[0])?.errors[0];
    },
  };
}
